import { SheetAttributeData, SheetAttributeMappedCells } from '../../../services/sheet-attribute-manager/models/sheet-attribute-manager.interface';
import { rowConfig as balanceSheetRowConfig } from '../balance-sheet/data';
import { NEGATIVE_PREFIX } from '../financials-widget-drawer/types';
import { rowConfig as incomeStatementRowConfig } from '../income-statement/data';

export interface MappingReferenceData {
  document_reference: number;
  field_name: string;
  sheet_name: string;
  cell_references: SheetAttributeMappedCells[];
}

export interface IncomeStatementReferences {
  [period: string]: {
    [fieldName: string]: MappingReferenceData[];
  };
}

export interface BalanceSheetReferences {
  [period: string]: {
    [fieldName: string]: MappingReferenceData[];
  };
}

export interface GeneratedReferences {
  incomeStatementReferences: IncomeStatementReferences;
  balanceSheetReferences: BalanceSheetReferences;
}

export enum MODEL_TYPE_VALUE_SET {
  INCOME_STATEMENT = 'income_statement',
  BALANCE_SHEET = 'balance_sheet'
}

export interface DocumentReferenceMetadata {
  documentIds: number[];
  metadata: {
      documentId: number;
      fields: {
          fieldName: string;
          sheetName: string;
          cellReferences: SheetAttributeMappedCells[];
      }[];
  }[];
}

class ReferenceGeneratorService {
  /**
 * Determine if a field belongs to income statement or balance sheet
 */
  getFieldModelType(fieldName: string): MODEL_TYPE_VALUE_SET | null {
    const incomeStatementAlias = new Set(incomeStatementRowConfig(false).map(item => item.alias));
    const balanceSheetAlias = new Set(balanceSheetRowConfig(false).map(item => item.alias));

    if (incomeStatementAlias.has(fieldName)) {
      return MODEL_TYPE_VALUE_SET.INCOME_STATEMENT;
    }
    if (balanceSheetAlias.has(fieldName)) {
      return MODEL_TYPE_VALUE_SET.BALANCE_SHEET;
    }

    return null;
  }



  /**
   * Generate mapping references from sheet attribute data
   * 
   * This mirrors the backend logic in reference_generator_service.py
   * It iterates over the data property and creates MappingReference objects for each
   * field/period/document combination.
   */
  generateMappingReferences(
    sheetAttributeData: SheetAttributeData
  ): GeneratedReferences {
    const incomeStatementReferences: IncomeStatementReferences = {};
    const balanceSheetReferences: BalanceSheetReferences = {};

    // Iterate over the data property (periods like "2023", "2024", "LTM", etc.)
    Object.keys(sheetAttributeData).forEach(periodIdentifier => {
      const fieldsData = sheetAttributeData[periodIdentifier];

      // For each row property determine if balance sheet or income statement
      Object.keys(fieldsData).forEach(fieldName => {
        const fieldData = fieldsData[fieldName];
        const cleanedFieldName = fieldName.includes(NEGATIVE_PREFIX) ? fieldName.split(NEGATIVE_PREFIX).at(1)! : fieldName
        const modelType = this.getFieldModelType(cleanedFieldName);

        if (!modelType) {
          return; // Skip unknown fields
        }

        // For each referenceData item (frontend uses camelCase)
        const referenceDataList = fieldData.referenceData || [];

        const mappingReferences: MappingReferenceData[] = [];

        referenceDataList.forEach(refDataItem => {
          const { documentReferenceId } = refDataItem;
          const sheetMappingConfig = refDataItem.sheetMappingConfig || {};

          if (!documentReferenceId) {
            return;
          }

          // For each sheetMappingConfig
          Object.keys(sheetMappingConfig).forEach(sheetName => {
            const sheetConfig = sheetMappingConfig[sheetName];
            const mappedCells = sheetConfig.mappedCells || [];

            if (mappedCells.length === 0) {
              return;
            }

            // Create a mapping reference
            mappingReferences.push({
              document_reference: documentReferenceId,
              field_name: cleanedFieldName,
              sheet_name: sheetName,
              cell_references: mappedCells,
            });
          });
        });

        // Store references in the appropriate structure
        if (modelType === 'income_statement') {
          if (!incomeStatementReferences[periodIdentifier]) {
            incomeStatementReferences[periodIdentifier] = {};
          }
          incomeStatementReferences[periodIdentifier][cleanedFieldName] = mappingReferences;
        } else if (modelType === 'balance_sheet') {
          if (!balanceSheetReferences[periodIdentifier]) {
            balanceSheetReferences[periodIdentifier] = {};
          }
          balanceSheetReferences[periodIdentifier][cleanedFieldName] = mappingReferences;
        }
      });
    });

    return {
      incomeStatementReferences,
      balanceSheetReferences,
    };
  }

  getDocumentReferences = (referenceList: MappingReferenceData[] = []): DocumentReferenceMetadata => {
    if (!referenceList || referenceList.length === 0) {
      return { documentIds: [], metadata: [] };
    }

    // Group references by document ID
    const docMap = new Map<number, MappingReferenceData[]>();
    referenceList.forEach(item => {
      const existing = docMap.get(item.document_reference) || [];
      docMap.set(item.document_reference, [...existing, item]);
    });

    // Build metadata for each document
    const metadata = Array.from(docMap.entries()).map(([documentId, refs]) => ({
      documentId,
      fields: refs.map(ref => ({
        fieldName: ref.field_name,
        sheetName: ref.sheet_name,
        cellReferences: ref.cell_references,
      })),
    }));

    return {
      documentIds: Array.from(docMap.keys()),
      metadata,
    };
  }

  /**
   * Get reference metadata for a specific cell
   * This will be attached to the cell to indicate it has a mapping reference
   */
  getReferenceMetadataForCell(
    period: string,
    fieldName: string,
    references: GeneratedReferences
  ): MappingReferenceData[] | null {
    // Check income statement references
    const incomeRefs = references.incomeStatementReferences[period]?.[fieldName];
    if (incomeRefs && incomeRefs.length > 0) {
      return incomeRefs;
    }

    // Check balance sheet references
    const balanceRefs = references.balanceSheetReferences[period]?.[fieldName];
    if (balanceRefs && balanceRefs.length > 0) {
      return balanceRefs;
    }

    return null;
  }

}



export default ReferenceGeneratorService;